import type {
	ExtractInjectionAPI,
	NextEditorPlugin,
	OptionalPlugin,
} from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection';
import type { AIPlugin, FAILURE_REASON } from '@atlassian/editor-plugin-ai';

export type API = ExtractInjectionAPI<AiStreamingOrchestratorPlugin>;

export type StreamingCommandFactory<T extends StreamingCommand> = (
	api: API,
) => (args: T) => boolean;

export type MultiFormatContentType = 'text/markdown' | 'text/html' | 'application/adf+json';

type StreamableCommandMetadata = {
	contentType?: MultiFormatContentType;
};

// Source https://bitbucket.org/atlassian/conversational-ai-platform/src/main/modules/product/rovo/rovo-impl/src/main/resources/yaml/actions/internal/aifc_confluence_page_update.yaml#lines-67
// replaceDoc replaces the entire document; there is no localId because the
// top-level doc node has no localId and there is only one.
// contentType must be 'text/html' or 'application/adf+json' — the parser
// rejects any other value.
export type ReplaceDocCommand = StreamableCommandMetadata & {
	noStream?: boolean;
	type: 'replaceDoc';
};
export type ReplaceNodeCommand = StreamableCommandMetadata & {
	localId: string;
	noStream?: boolean;
	type: 'replaceNode';
};
export type InsertNodeBeforeCommand = StreamableCommandMetadata & {
	localId: string;
	noStream?: boolean;
	type: 'insertNodeBefore';
};
export type InsertNodeAfterCommand = StreamableCommandMetadata & {
	localId: string;
	noStream?: boolean;
	type: 'insertNodeAfter';
};
export type AppendNodeCommand = StreamableCommandMetadata &
	(
		| {
				// streamingType is used to show the highlight or not while streaming. In the create flow we don't want to show the highlight
				// as it is distracting when updating large documents
				streamingType?: 'create' | 'update';
				type: 'appendNode';
		  }
		| {
				// streamingType is used to show the highlight or not while streaming. In the create flow we don't want to show the highlight
				// as it is distracting when updating large documents
				streamingType?: 'create' | 'update';
				type: 'appendNodeToEnd';
		  }
	);

export type RemoveNodeCommand = { localId: string; type: 'removeNode' };
export type RemoveNodesCommand = { localIds: string[]; type: 'removeNodes' };
export type MoveNodeCommand = {
	anchorLocalId: string;
	localId: string;
	relationship: 'before' | 'after';
	type: 'moveNode';
};
export type SetAttrsCommand = {
	attrs: Record<string, unknown>;
	localId: string;
	type: 'setAttrs';
};

export type WrapNodeCommand =
	| {
			localId: string;
			type: 'wrap';
			wrapperAttrs: Record<string, unknown>;
			wrapperType: string;
	  }
	| {
			localId: string;
			type: 'wrapNode';
			wrapperAttrs: Record<string, unknown>;
			wrapperType: string;
	  };

export type StreamableCommandWithValue =
	| ReplaceDocCommand
	| ReplaceNodeCommand
	| InsertNodeBeforeCommand
	| InsertNodeAfterCommand
	| AppendNodeCommand;

// #region Public APIs
export type StreamableCommand = Exclude<StreamableCommandWithValue, 'value'>;

export type NonStreamableCommand =
	| RemoveNodeCommand
	| RemoveNodesCommand
	| MoveNodeCommand
	| WrapNodeCommand
	| SetAttrsCommand;

export type StreamingCommand = StreamableCommand | NonStreamableCommand;

export const allStreamingCommands = [
	'replaceDoc',
	'replaceNode',
	'removeNode',
	'removeNodes',
	'moveNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'appendNodeToEnd',
	'setAttrs',
	'wrap',
	'wrapNode',
	'appendNode',
] as const;

export type StreamingError = {
	apiName?: string;
	errorContent?: string;
	errorKey?: string;
	failureReason: FAILURE_REASON;
	statusCode?: number;
};

export type AiStreamingOrchestratorPluginOptions = {
	streamFlowPacerEnabled?: boolean;
};

export type AiStreamingOrchestratorPlugin = NextEditorPlugin<
	'aiStreamingOrchestrator',
	{
		actions: {
			/**
			 * Abort the streaming immediately and end the experience.
			 * Rather than finishing the current streaming, this will discard any in-progress streaming
			 * and end the experience. No further streaming can be done until a new action is initialized.
			 */
			abortSession: () => void;
			endSession: (error?: StreamingError) => Promise<void>;
			finishAction: (actionId: string, error?: StreamingError) => void;
			initAction: (
				command: StreamingCommand,
				options?: {
					hideLoadingScreen?: boolean;
				},
			) => string;
			startSession: (
				traceId?: string,
				metadata?: { promptVersion?: string; singleInstrumentationId?: string },
			) => string;
			streamValue: (actionId: string, chunk: string) => void;
		};
		dependencies: [
			AIPlugin,
			OptionalPlugin<LocalIdPlugin>,
			OptionalPlugin<AnalyticsPlugin>,
			OptionalPlugin<SelectionPlugin>,
		];
		pluginConfiguration?: AiStreamingOrchestratorPluginOptions;
	}
>;
// #endregion
